export interface TenantHostConfig {
  domainSuffix: string;
  reservedSubdomains: Set<string>;
  demoHost: string | null;
  demoSlug: string | null;
}

const DEFAULT_RESERVED = 'talent,kpl,interview';

export function loadTenantHostConfig(
  env: Record<string, string | undefined>,
): TenantHostConfig {
  const domainSuffix = env.TENANT_DOMAIN_SUFFIX ?? '.gyx.ireborn.com.cn';
  const reserved = new Set(
    (env.TENANT_RESERVED_SUBDOMAINS ?? DEFAULT_RESERVED)
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean),
  );
  const demoHost = env.TENANT_DEMO_HOST?.trim() || null;
  const demoSlug = env.TENANT_DEMO_SLUG?.trim() || null;
  return { domainSuffix, reservedSubdomains: reserved, demoHost, demoSlug };
}

export function normalizeHost(host?: string | null): string | null {
  if (!host) return null;
  const trimmed = host.split(',')[0]?.trim().toLowerCase();
  if (!trimmed) return null;
  return trimmed.split(':')[0] || null;
}

export function tenantSlugFromHostWithConfig(
  host: string | null | undefined,
  cfg: TenantHostConfig,
): string | null {
  const h = normalizeHost(host);
  if (!h) return null;

  if (h === 'localhost' || h === '127.0.0.1') return null;

  if (cfg.demoHost && h === cfg.demoHost && cfg.demoSlug) {
    return cfg.demoSlug;
  }

  if (!h.endsWith(cfg.domainSuffix)) return null;

  const sub = h.slice(0, -cfg.domainSuffix.length);
  if (!sub || sub.includes('.') || cfg.reservedSubdomains.has(sub)) return null;
  if (cfg.demoSlug && sub === 'talent') return cfg.demoSlug;
  if (!/^[a-z0-9-]+$/.test(sub)) return null;
  return sub;
}

export function tenantHostForSlugWithConfig(slug: string, cfg: TenantHostConfig): string {
  if (cfg.demoSlug && slug === cfg.demoSlug && cfg.demoHost) return cfg.demoHost;
  return `${slug}${cfg.domainSuffix}`;
}

export function publicOriginFromHost(host?: string | null): string | null {
  const h = normalizeHost(host);
  if (!h || h === 'localhost' || h === '127.0.0.1') return null;
  return `https://${h}`;
}
